import 'remixicon/fonts/remixicon.css';
import React from 'react';
import { useAppDispatch } from '../../store/Hooks';
import { fetchResults } from '../../store/StudentSlice';
import { useAppSelector } from '../../store/Hooks';
import { Task } from '../../store/viewmodels/TaskViewModel';
import { Itemset } from '../../store/viewmodels/ItemsetViewModel';
import { StudentData } from '../../store/viewmodels/StudentViewModel';
import { AnswerSetResult } from '../../store/viewmodels/AnswerSetViewModel';
import { newAsLoaded } from '../../store/AnswerSetSlice';
import { Link } from 'react-router-dom';
import { isTaskDone } from '../../utils/TaskNavigationServices';
import { getItemsetLogos, getItemsetStar, getTaskLogos, getTaskStars, isItemsetDone } from '../utils/Accessories';

interface TaskResultsProps {
    task: Task,
}

function TaskResults(props: TaskResultsProps): JSX.Element {
    const dispatch = useAppDispatch()
    const studentData: StudentData = useAppSelector(state => state.studentData);
    const newAsToLoad: boolean | undefined = useAppSelector(state => state.answerSet.new);

    React.useEffect(() => {
        if (newAsToLoad) {
            dispatch(fetchResults(studentData.student.id))
                .then(() => {
                    dispatch(newAsLoaded())
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const studentsAnswerSets = studentData.student.results;
    const taskAccessList = studentData.student.task_access_list;
    const itemsetAccessList = studentData.student.active_item_sets;

    if (studentsAnswerSets === undefined) {
        return (
            <div className="flex mb-4">
                {getTaskLogos(props.task)}
                <h1 className="ml-2 text-2xl font-medium text-teal-600 my-auto">{props.task.name}</h1>
            </div>
        );
    }
    else {
        let tasksItemsets: Itemset[] = [] as Itemset[];

        if (taskAccessList.includes(props.task)) {
            let itemsets = props.task.itemsets;

            for (let i: number = 0; i < itemsets.length; i++) {
                for (let ir: number = 0; ir < itemsetAccessList.length; ir++) {
                    if (itemsets[i].id === itemsetAccessList[ir].id && itemsets[i].ready_to_publish) {
                        tasksItemsets.push(itemsets[i]);
                    }
                }
            }
        }

        let tasksAnswers: AnswerSetResult[] = [] as AnswerSetResult[];

        for (let i: number = 0; i < tasksItemsets.length; i++) {
            for (let a: number = 0; a < studentsAnswerSets.length; a++) {

                if (studentsAnswerSets[a].itemset?.id && (tasksItemsets[i].id === studentsAnswerSets[a].itemset.id)) {
                    tasksAnswers.push(studentsAnswerSets[a]);
                } else if (studentsAnswerSets[a] === null || undefined) {
                    console.log(studentsAnswerSets[a])
                }
            }
        }

        const oneTimeOnly = props.task.one_time_only;
        const isDone = oneTimeOnly ? isTaskDone(props.task, studentData.student) : false;

        let oneTimeHeader = oneTimeOnly ? <div>Tämän tehtäväkokonaisuuden voi suorittaa vain kerran.</div> : "";

        let itemsetsElement = isDone ? <div>Olet tehnyt jo nämä tehtävät.</div> : <ItemsetList answerSets={tasksAnswers} itemsets={tasksItemsets} task={props.task} />
        let starElement = getTaskStars(props.task, tasksAnswers, tasksItemsets)

        return (
            <div>
                <div className="flex mb-4">
                    {getTaskLogos(props.task)}
                    <h1 className="ml-2 text-2xl 2xl:text-3xl font-medium text-teal-600 my-auto">{props.task.name}</h1>
                    {starElement}
                </div>
                <div>{oneTimeHeader}</div>
                <div className="py-4">
                    <h2 className="py-2 text-xl 2xl:text-2xl font-medium text-teal-600 my-auto">Tehtävät</h2>
                    {itemsetsElement}
                </div>
            </div>
        );
    }
}

interface ItemsetListProps {
    task: Task
    itemsets: Itemset[],
    answerSets: AnswerSetResult[]
}

function ItemsetList(props: ItemsetListProps): JSX.Element {
    let listElements = [];
    let orderedItemsets = props.itemsets.sort(function (a, b) {
        if (a.name > b.name) return 0;
        return -1;
    })

    for (let i: number = 0; i < orderedItemsets.length; i++) {
        listElements.push(<ItemsetInfo key={"itemsetinfo" + props.itemsets[i].id} answerSets={props.answerSets} itemset={props.itemsets[i]} task={props.task} />);
    }

    return (
        <div>{listElements}</div>
    );
}

interface ItemsetInfoProps {
    task: Task,
    itemset: Itemset,
    answerSets: AnswerSetResult[]
}

function ItemsetInfo(props: ItemsetInfoProps): JSX.Element {
    let symbols: JSX.Element[] = getItemsetLogos(props.itemset);
    let taskLink = "/content/tasks/" + props.task.id;
    let disableButton = (
        <button key={props.itemset.id + props.itemset.name} className="pointer-events-none p-2 inline-flex items-center px-3 py-0 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-500">
            <span>Tehty </span>
            <span className="ri-thumb-up-fill"></span>
        </button>
    );

    let okButton = (
        <Link key={props.itemset.name + props.itemset.id} to={taskLink + "/itemsets/" + props.itemset.id}>
            <button key={props.itemset.id + props.itemset.name} className="p-2 lg:py-1 inline-flex items-center border border-transparent text-base 3xl:text-lg font-medium rounded-md shadow-sm text-white bg-teal-500 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <span>Aloita </span>
                <span className="ri-arrow-right-fill"></span>
            </button>
        </Link>
    );

    let itemsetDone = isItemsetDone(props.answerSets, props.itemset);

    return (
        <div className="flex my-1 ">
            <div className="w-full lg:w-2/3 xl:w-1/2 5xl:w-1/3 8xl:w-1/4 flex gap-x-4">
                <div className="flex-none justify-center my-auto">
                    <div className="lg:flex justify-center my-auto 2xl:text-xl">
                        {getItemsetStar(props.task, props.itemset, props.answerSets)}
                        {props.itemset.name}
                        <div className="ml-5 xl:ml-4 flex my-auto xl:justify-center"> {symbols}</div>
                    </div>
                </div>
                <div className=" w-full h-1/2 border-b-2 border-dotted border-teal-500  "></div>
            </div>
            <div className="ml-2 my-auto">
                {(props.task.one_time_only && itemsetDone) ? disableButton : okButton}
            </div>
        </div>
    );
}

export default TaskResults;